import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { createContext, memo, type ReactNode, useMemo, useState } from "react";

/** The tab the user is on, as far as the table needs to know. */
interface SelectedRecordViewTab {
  /** A built-in tab's own filters, so the table knows they aren't the user's change. */
  filters?: AdvancedFilterData[];
  /** All, Needs action, Flagged and other built-ins: never edited or deleted, only saved as a new view. */
  locked: boolean;
}

/** What the view tabs and the table under them tell each other. */
interface RecordViewTabsChannel {
  /** A new symbol per + click; the table opens its Create view modal when it changes. */
  createRequest?: symbol;
  requestCreate(): void;
  /** The table calls this once it has opened the modal, so a later remount
   * of the table (switching views, say) doesn't open it again. */
  clearCreateRequest(): void;
  selectedTab?: SelectedRecordViewTab;
  setSelectedTab(tab: SelectedRecordViewTab | undefined): void;
}

export const RecordViewTabsContext = createContext<
  RecordViewTabsChannel | undefined
>(undefined);

export const RecordViewTabsProvider = memo(function RecordViewTabsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [createRequest, setCreateRequest] = useState<symbol>();
  const [selectedTab, setSelectedTab] = useState<SelectedRecordViewTab>();
  const value = useMemo(
    (): RecordViewTabsChannel => ({
      createRequest,
      requestCreate: () => setCreateRequest(Symbol("create view")),
      clearCreateRequest: () => setCreateRequest(undefined),
      selectedTab,
      setSelectedTab,
    }),
    [createRequest, selectedTab],
  );
  return (
    <RecordViewTabsContext.Provider value={value}>
      {children}
    </RecordViewTabsContext.Provider>
  );
});
